import { ForwardedRef, forwardRef, useImperativeHandle, useRef, useState } from "react"
import { AcceptButton, DenyButton } from "../../Orders/Components/Form/Buttons/Buttons"
import { ModulePopup } from "../../Orders/Components/ModulePopup/ModulePopup"
import { SbtH1 } from "../../Orders/Components/Text/SbtH1/SbtH1"
import { cls } from "../cls"
import style from "./ConfirmModal.module.css"

export enum ConfirmModalCloseReason {
	Close = "Close",
	Deny = "Deny",
	Accept = "Accept",
}

export type ConfirmModalProps = {
	onClose: (reason: ConfirmModalCloseReason) => void
	text: string
	customContent: JSX.Element | string | null
	className?: string
	closedByDefault?: boolean
}

export type ConfirmModalRefProps = {
	open: (
		text: string,
		customContent: JSX.Element | string | null,
		onClose: ((reason: ConfirmModalCloseReason) => void) | null,
	) => void
	close: () => void
}

export const ConfirmModal = forwardRef((props: ConfirmModalProps, ref: ForwardedRef<ConfirmModalRefProps>) => {
	const [showModal, setShowModal] = useState(props.closedByDefault !== undefined ? !props.closedByDefault : true)
	const [text, setText] = useState<string>(props.text)
	const [customContent, setCustomContent] = useState<JSX.Element | string | null>(props.customContent)

	const customOnCloseCallback = useRef<((reason: ConfirmModalCloseReason) => void) | null>(null)

	useImperativeHandle(
		ref,
		() => ({
			open(
				text: string,
				customContent: JSX.Element | string | null,
				onClose: ((reason: ConfirmModalCloseReason) => void) | null,
			) {
				setShowModal(true)
				setText(text)
				setCustomContent(customContent)
				customOnCloseCallback.current = onClose
			},
			close() {
				customOnCloseCallback.current = null
				setShowModal(false)
			},
		}),
		[],
	)

	if (!showModal) {
		return null
	}

	return (
		<ModulePopup
			className={cls(props.className, style.modal)}
			onClose={() => {
				const customCallback = customOnCloseCallback.current
				if (customCallback) {
					customCallback(ConfirmModalCloseReason.Close)
					customOnCloseCallback.current = null
				}

				props.onClose(ConfirmModalCloseReason.Close)
			}}>
			<div className={style.wrapper}>
				<SbtH1>Är du säker?</SbtH1>
				<div className={style.content}>{text}</div>
				{customContent ? <div className={style.customContent}>{customContent}</div> : null}
				<div className={style.buttons}>
					<AcceptButton
						onClick={() => {
							const customCallback = customOnCloseCallback.current
							if (customCallback) {
								customCallback(ConfirmModalCloseReason.Accept)
								customOnCloseCallback.current = null
							}

							props.onClose(ConfirmModalCloseReason.Accept)
						}}>
						Ja
					</AcceptButton>
					<DenyButton
						onClick={() => {
							const customCallback = customOnCloseCallback.current
							if (customCallback) {
								customCallback(ConfirmModalCloseReason.Deny)
								customOnCloseCallback.current = null
							}

							props.onClose(ConfirmModalCloseReason.Deny)
						}}>
						Nej
					</DenyButton>
				</div>
			</div>
		</ModulePopup>
	)
})
